import React, { useState } from 'react';

import { cssMap } from '@atlaskit/css';
import CopyIcon from '@atlaskit/icon/core/copy';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Inline, Pressable, xcss } from '@atlaskit/primitives';
import { Flex } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import CopyToClipboard, { type CopyToClipboardProps } from '../../copy-to-clipboard';

const iconSpacingStyles = cssMap({
	space050: {
		paddingBlock: token('space.050'),
		paddingInline: token('space.050'),
	},
});

type CodeCopyButtonProps = {
	children: React.ReactNode;
	onClick: () => void;
};

const buttonStyles = xcss({
	textAlign: 'left',
	width: '100%',
	padding: 'space.0',
	borderRadius: 'radius.xsmall',
	backgroundColor: 'color.background.neutral',
});

const copyIconStyles = xcss({
	visibility: 'hidden',
});

const containerStyles = xcss({
	flexGrow: 1,
	color: 'color.text',
	padding: 'space.100',
	font: 'font.code',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	lineHeight: token('space.250'),
});

/**
 * __Code copy__
 *
 * A code component which allows for a statement to be copied to the clipboard.
 */
const CodeCopyButton = ({ children, onClick }: CodeCopyButtonProps) => {
	const [isMouseOver, setIsMouseOver] = useState(false);

	const mouseEnterHandler = () => {
		setIsMouseOver(true);
	};

	const mouseLeaveHandler = () => {
		setIsMouseOver(false);
	};

	return (
		<Pressable
			xcss={buttonStyles}
			onMouseEnter={mouseEnterHandler}
			onMouseLeave={mouseLeaveHandler}
			onClick={() => onClick()}
		>
			<Inline>
				<Box xcss={containerStyles}>{children}</Box>
				<Box padding="space.075" xcss={!isMouseOver && copyIconStyles}>
					<Flex xcss={iconSpacingStyles.space050}>
						<CopyIcon label="" color="currentColor" />
					</Flex>
				</Box>
			</Inline>
		</Pressable>
	);
};

type CodeCopyToClipboardProps = {
	value: string;
	children: React.ReactNode;
	messages: CopyToClipboardProps['messages'];
};

/**
 * __Code copy to clipboard__
 *
 * A component that allows you to copy code to the clipboard.
 */
const CodeCopyToClipboard = ({
	value,
	children,
	messages,
}: CodeCopyToClipboardProps): React.JSX.Element => {
	return (
		<CopyToClipboard value={value} messages={messages} resetMs={2000}>
			{({ copy }) => <CodeCopyButton onClick={copy} children={children} />}
		</CopyToClipboard>
	);
};

export default CodeCopyToClipboard;
